import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Paper, Stack, LinearProgress, Alert, Snackbar } from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useAuth } from '../components/AuthContext';
import { API_BASE_URL } from '../components/api.js';

const PontoCSV = () => {
    const [file, setFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [status, setStatus] = useState({ open: false, message: '', severity: 'info' });
    const fileInputRef = useRef(null);
    const timerRef = useRef(null);

    const { authenticatedFetch, logout } = useAuth();

    const startTimer = () => {
        clearInterval(timerRef.current);
        setElapsedTime(0);
        timerRef.current = setInterval(() => setElapsedTime(prev => prev + 1), 1000);
    };

    const stopTimer = () => clearInterval(timerRef.current);

    const processFile = async (selectedFile) => {
        if (!selectedFile.name.endsWith('.csv')) {
            setStatus({ open: true, message: 'Por favor, selecione apenas arquivos .CSV', severity: 'error' });
            return;
        }

        setIsLoading(true);
        startTimer();

        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            const response = await authenticatedFetch(`${API_BASE_URL}/api/ponto-importar-csv/`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (!response.ok) throw new Error(data.error || 'Erro ao processar arquivo.');

            setStatus({ 
                open: true, 
                message: `Sucesso! ${data.importados} registros processados.`, 
                severity: 'success' 
            });
            setFile(null);
        } catch (err) {
            if (err.message.includes('Token')) {
                logout();
            } else {
                setStatus({ open: true, message: err.message, severity: 'error' });
            }
        } finally {
            setIsLoading(false);
            stopTimer();
        }
    };

    const handleFileChange = (e) => {
        const uploadedFile = e.target.files[0];
        if (uploadedFile) {
            setFile(uploadedFile);
            processFile(uploadedFile);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        if (isLoading) return;
        const uploadedFile = e.dataTransfer.files[0];
        if (uploadedFile) {
            setFile(uploadedFile);
            processFile(uploadedFile);
        }
    };

    useEffect(() => {
        return () => clearInterval(timerRef.current);
    }, []);

    return (
        <Box sx={{ p: 3, maxWidth: '800px', mx: 'auto' }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>Importar Ponto via CSV</Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
                O CSV deve conter as colunas: <strong>Data, Nome, Entrada, Almoço, Volta, Saída, Observação</strong>.
                <br />O nome do funcionário deve ser idêntico ao cadastrado no sistema.
            </Alert>

            <Paper
                variant="outlined"
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => !isLoading && fileInputRef.current.click()}
                sx={{
                    p: 6, textAlign: 'center', cursor: isLoading ? 'default' : 'pointer',
                    border: '2px dashed', borderColor: isLoading ? 'primary.main' : 'text.secondary',
                    backgroundColor: 'background.paper', transition: '0.2s',
                    '&:hover': { backgroundColor: 'action.hover', borderColor: 'primary.main' }
                }}
            >
                <input type="file" hidden ref={fileInputRef} onChange={handleFileChange} accept=".csv" />
                
                <Stack spacing={2} alignItems="center">
                    <CloudUploadIcon sx={{ fontSize: 50, color: 'primary.main' }} />
                    <Typography variant="h6">
                        {file ? file.name : 'Arraste o arquivo de Ponto aqui ou clique'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Somente arquivos .CSV</Typography>
                </Stack>

                {isLoading && (
                    <Box sx={{ width: '100%', mt: 3 }}>
                        <LinearProgress />
                        <Typography variant="body2" sx={{ mt: 1 }}>Processando... {elapsedTime}s</Typography>
                    </Box>
                )}
            </Paper>

            <Snackbar 
                open={status.open} 
                autoHideDuration={6000} 
                onClose={() => setStatus({ ...status, open: false })}
            >
                <Alert severity={status.severity} variant="filled">{status.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default PontoCSV;